import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type IPopperProps, Arrow, Manager, Popper, Target } from 'react-popper';
import Transition from 'react-transition-group/Transition';
import PropTypes from 'prop-types';
import styled, { ThemeConsumer } from 'styled-components';

import { Color } from '@rover/kibble/styles';
import { onSpaceOrEnterKeyPress, onSpecifiedKeyPress } from '@rover/react-lib/src/utils/input';
import { isHoverable } from '@rover/rsdk/src/modules/Network/userAgent';

import { PopoverEvent, PopoverPlacement } from './popover.constants';
import StyledPopover, { TRANSITION_DURATION } from './StyledPopover';
import TargetWrapper from './TargetWrapper';

export const MOUSE_LEAVE_DELAY = 100;
type RenderProp = (arg0: { onClose: () => void }) => ReactNode;
export type Props = {
  id?: string;
  children: ReactNode;
  className?: string;
  content: ReactNode | RenderProp;
  open?: boolean;
  placement?: PopoverPlacement;
  event?: PopoverEvent;
  backgroundColor?: Color | string;
  borderColor?: Color | string;
  display?: 'inline' | 'inline-block' | 'block';
  showArrow?: boolean;
  toggleOnFocusOrBlur?: boolean;
  toggleOnSpaceOrEnter?: boolean;
  onChange?: (arg0: boolean) => void;
  role?: string;
  maxWidth?: string;
  preventOverflow?: Exclude<IPopperProps['modifiers'], undefined>['preventOverflow'];
};
export type PopperObj = {
  popper: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getArrowStyle: (...args: any[]) => any;
  };
};
type PopoverArrowContextProps = Props & {
  innerRef: (arg0: HTMLDivElement | null | undefined) => void;
  transitionState: string;
};

const PopoverArrowContext = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { innerRef, content, ...other }: PopoverArrowContextProps,
  { popper }: PopperObj
): ReactElement => (
  // @ts-expect-error fixing the odd TS errors from styled-components on Popover is tricky and not worth fixing because we need to update the Popover lib anyway
  <StyledPopover arrowStyle={popper.getArrowStyle()} {...other} ref={innerRef} />
);

PopoverArrowContext.contextTypes = {
  popper: PropTypes.object.isRequired,
};
export const ContentWrapper = styled.div<Record<string, unknown>>`
  background: rgba(0, 0, 0, 0);
  -webkit-font-smoothing: antialiased;
  /* this max-width helps prevent Popovers from being wider than the screen, which can trigger a resize loop */
  max-width: calc(100vw - 60px);
`;
export const StyledArrow = styled(Arrow)<Record<string, unknown>>`
  position: absolute;
`;

// Define the type for the component with static properties
type PopoverComponent = React.FC<Props> & {
  Event: typeof PopoverEvent;
  Placement: typeof PopoverPlacement;
};

const PopoverInner: React.FC<Props> = (props): ReactElement => {
  const {
    children,
    content,
    placement = PopoverPlacement.BOTTOM,
    display = 'inline',
    preventOverflow = { enabled: false },
    event = PopoverEvent.HOVER,
    open: openProp,
    backgroundColor,
    borderColor,
    showArrow = true,
    toggleOnFocusOrBlur = false,
    toggleOnSpaceOrEnter = false,
    onChange,
    id,
    ...other
  } = props;

  const [open, setOpen] = useState(false);
  const removeListenerRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverRef = useRef<HTMLElement | null>(null);

  const isOpen = useCallback((): boolean | undefined => {
    return event === PopoverEvent.NONE ? openProp : open;
  }, [event, openProp, open]);

  const clearTimeoutFn = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleOpen = useCallback((): void => {
    if (event !== PopoverEvent.NONE) {
      if (onChange) onChange(true);
      setOpen(true);
    }
  }, [event, onChange]);

  const handleClose = useCallback((): void => {
    if (event !== PopoverEvent.NONE) {
      if (onChange) onChange(false);
      setOpen(false);
    }
  }, [event, onChange]);

  const handleToggle = useCallback((): void => {
    if (event !== PopoverEvent.NONE) {
      setOpen((prevOpen) => {
        if (onChange) onChange(!prevOpen);
        return !prevOpen;
      });
    }
  }, [event, onChange]);

  const handleFocusOutsideOfTooltip = useCallback(
    (focusEvent: FocusEvent): void => {
      // if keyboard focus is transferred to an element outside the tooltip, close it
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (popoverRef.current && !popoverRef.current.contains(focusEvent.relatedTarget as any)) {
        handleClose();
        if (process.env.JS_ENV_CLIENT)
          document.removeEventListener('focusout', handleFocusOutsideOfTooltip, false);
      }
    },
    [handleClose]
  );

  const handleOutsideClick = useCallback(
    (clickEvent: Event): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (popoverRef.current && !popoverRef.current.contains(clickEvent.target as any)) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleClick = useCallback((): void => {
    if (!toggleOnFocusOrBlur && (event === PopoverEvent.CLICK || !isHoverable())) {
      handleToggle();

      if (!isOpen()) {
        setTimeout(() => {
          document.addEventListener('click', handleOutsideClick, false);
        }, 0);

        removeListenerRef.current = (): void =>
          document.removeEventListener('click', handleOutsideClick, false);
      }
    }
  }, [toggleOnFocusOrBlur, event, handleToggle, isOpen, handleOutsideClick]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      // allow Esc to close the popover if open
      if (isOpen()) {
        onSpecifiedKeyPress(e, ['Escape'], handleToggle);
      }
    },
    [isOpen, handleToggle]
  );

  const handleMouseEnter = useCallback((): void => {
    if (event === PopoverEvent.HOVER && isHoverable()) {
      clearTimeoutFn();
      handleOpen();
    }
  }, [event, clearTimeoutFn, handleOpen]);

  const handleMouseLeave = useCallback((): void => {
    if (event === PopoverEvent.HOVER && isHoverable()) {
      clearTimeoutFn();
      timeoutRef.current = setTimeout(() => {
        handleClose();
        timeoutRef.current = null;
      }, MOUSE_LEAVE_DELAY);
    }
  }, [event, clearTimeoutFn, handleClose]);

  const renderContent = useCallback((): ReactNode => {
    if (typeof content === 'function') {
      return content({
        onClose: handleClose,
      });
    }

    return content;
  }, [content, handleClose]);

  // Effect to handle cleanup on unmount
  useEffect(() => {
    return (): void => {
      clearTimeoutFn();

      if (removeListenerRef.current) {
        removeListenerRef.current();
      }
    };
  }, [clearTimeoutFn]);

  // Effect to clean up listener when popover closes
  useEffect(() => {
    if (!isOpen() && removeListenerRef.current) {
      removeListenerRef.current();
      removeListenerRef.current = null;
    }
  }, [isOpen]);

  const eventHandlers: Record<string, unknown> = {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    // we need to use onKeyDown to catch Esc as onKeyPress doesn't detect it
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  if (toggleOnSpaceOrEnter) {
    eventHandlers.onKeyPress = (e: KeyboardEvent): void => onSpaceOrEnterKeyPress(e, handleToggle);
  }

  if (toggleOnFocusOrBlur) {
    eventHandlers.onFocus = (): void => {
      if (!open) {
        handleToggle();
        // add a document-level blur handler which will close the tooltip if focus goes outside of it
        // (we use focusout instead of blur because blur doesn't bubble up from the trigger element)
        if (process.env.JS_ENV_CLIENT)
          document.addEventListener('focusout', handleFocusOutsideOfTooltip, false);
      }
    };
  }

  return (
    // ... in your render method selection:
    <ThemeConsumer>
      {(theme) => (
        <Manager tag={display === 'inline' ? 'span' : 'div'}>
          <Target
            {...eventHandlers}
            component={TargetWrapper}
            event={event}
            display={display}
            data-testid="styled-wrapper"
            id={id}
          >
            {children}
          </Target>
          <Transition in={isOpen()} timeout={TRANSITION_DURATION} unmountOnExit>
            {(state): JSX.Element => (
              <Popper
                // @ts-expect-error
                placement={placement.toString()}
                modifiers={{
                  offset: {
                    offset: -10,
                  },
                  preventOverflow,
                  hide: {
                    enabled: false,
                  },
                }}
              >
                {({
                  popperProps: { ref, 'data-x-out-of-boundaries': dataXOutOfBoundaries, ...rest },
                }): JSX.Element => {
                  const isOutOfBoundaries = typeof dataXOutOfBoundaries !== 'undefined';
                  return (
                    <PopoverArrowContext
                      // @ts-expect-error
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      placement={placement}
                      transitionState={state}
                      innerRef={(p): void => {
                        if (p) {
                          popoverRef.current = p;
                          ref(p);
                        }
                      }}
                      isOutOfBoundaries={isOutOfBoundaries}
                      backgroundColor={backgroundColor ?? theme?.background.primary}
                      borderColor={borderColor ?? theme?.colors.border.primary}
                      showArrow={showArrow}
                      theme={theme}
                      {...rest}
                      {...other}
                    >
                      <ContentWrapper>{renderContent()}</ContentWrapper>
                      <StyledArrow />
                    </PopoverArrowContext>
                  );
                }}
              </Popper>
            )}
          </Transition>
        </Manager>
      )}
    </ThemeConsumer>
  );
};

// Create the Popover component with static properties
const Popover = PopoverInner as PopoverComponent;

// Display name for debugging
Popover.displayName = 'Popover';

// Static properties for backward compatibility
Popover.Event = PopoverEvent;
Popover.Placement = PopoverPlacement;

export default Popover; // If we export this directly on top when we instantiate the variable, styleguide would endup using
// this component's name as displayname in the styleguide example.
// see warning section for more info: https://react-styleguidist.js.org/docs/components.html#default-vs-named-exports

export { PopoverArrowContext };
